import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useOrinAdmin } from "@/hooks/useOrinAdmin";
import { Card, CardContent } from "@/components/ui/Card";
import { Avatar } from "@/components/ui/Avatar";
import { cn } from "@/lib/utils";
import {
  AlertCircle,
  AlertTriangle,
  Bot,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Inbox,
  Search,
  Send,
  ShieldCheck,
  Wrench,
} from "lucide-react";

type Tab = "inbox" | "issues" | "compose";

interface Thread {
  counterparty: string;
  counterparty_name: string;
  last_message_at: string;
  unread_count: number;
  last_body_snippet: string;
  channel: string;
}

interface ToolCall {
  name: string;
  args?: unknown;
  result?: unknown;
  error?: string;
}

interface Message {
  id: string;
  direction: "inbound" | "outbound";
  channel: "chat" | "slack" | "email";
  body: string;
  created_at: string;
  tool_calls?: ToolCall[];
  metadata?: Record<string, unknown>;
}

interface IssueGrant {
  id: string;
  chat_user_resource: string;
  guessed_email?: string;
  display_name?: string;
  requested_at: string;
}

interface IssueToolError {
  id: string;
  tool: string;
  args?: unknown;
  error: string;
  occurred_at: string;
  counterparty?: string;
}

interface IssuesResponse {
  pending_grants: IssueGrant[];
  tool_errors: IssueToolError[];
}

interface SendResponse {
  ok: boolean;
  message_id: string;
  channel: string;
  sent_at: string;
}

function timeAgo(iso?: string) {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return "just now";
  const m = Math.round(ms / 60000);
  if (m < 1) return "now";
  if (m < 60) return `${m}m`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.round(h / 24);
  return `${d}d`;
}

function ChannelChip({ channel }: { channel: string }) {
  const meta: Record<string, { label: string; cls: string }> = {
    chat: { label: "Chat", cls: "bg-blue-50 text-blue-700" },
    slack: { label: "Slack", cls: "bg-purple-50 text-purple-700" },
    email: { label: "Email", cls: "bg-emerald-50 text-emerald-700" },
  };
  const m = meta[channel] ?? { label: channel, cls: "bg-muted text-muted-foreground" };
  return <span className={cn("rounded-full px-1.5 py-0.5 text-[10px] font-medium", m.cls)}>{m.label}</span>;
}

function ToolCallCard({ tc }: { tc: ToolCall }) {
  const [open, setOpen] = useState(false);
  const failed = !!tc.error;
  return (
    <div className={cn("my-1 rounded-md border text-micro", failed ? "border-destructive/30 bg-destructive/5" : "border-border bg-muted/40")}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 px-2 py-1.5 text-left"
      >
        {open ? <ChevronDown size={11} className="shrink-0" /> : <ChevronRight size={11} className="shrink-0" />}
        <Wrench size={10} className="shrink-0 text-muted-foreground" />
        <span className="font-mono text-[11px] text-foreground">{tc.name}</span>
        {failed && <AlertCircle size={10} className="shrink-0 text-destructive" />}
      </button>
      {open && (
        <div className="border-t border-border/60 px-2 py-1.5 font-mono text-[11px] text-muted-foreground">
          {tc.args !== undefined && (
            <div className="mb-1">
              <div className="text-[10px] uppercase tracking-wider text-foreground/50">args</div>
              <pre className="overflow-x-auto whitespace-pre-wrap break-words">{JSON.stringify(tc.args, null, 2)}</pre>
            </div>
          )}
          {tc.result !== undefined && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-foreground/50">result</div>
              <pre className="overflow-x-auto whitespace-pre-wrap break-words">{JSON.stringify(tc.result, null, 2)}</pre>
            </div>
          )}
          {tc.error && (
            <div className="text-destructive">{tc.error}</div>
          )}
        </div>
      )}
    </div>
  );
}

function MessageBubble({ msg }: { msg: Message }) {
  const isOut = msg.direction === "outbound";
  return (
    <div className={cn("flex flex-col gap-1", isOut ? "items-end" : "items-start")}>
      <div className={cn(
        "max-w-[80%] rounded-2xl px-3 py-2 text-caption shadow-sm",
        isOut ? "bg-primary text-white" : "bg-muted text-foreground"
      )}>
        <div className="whitespace-pre-wrap break-words">{msg.body}</div>
        {msg.tool_calls && msg.tool_calls.length > 0 && (
          <div className="mt-1.5 space-y-1">
            {msg.tool_calls.map((tc, i) => <ToolCallCard key={i} tc={tc} />)}
          </div>
        )}
      </div>
      <div className="flex items-center gap-1.5 px-1 text-[10px] text-muted-foreground">
        <ChannelChip channel={msg.channel} />
        <span>{new Date(msg.created_at).toLocaleString()}</span>
      </div>
    </div>
  );
}

function InboxTab() {
  const [selected, setSelected] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const bottomRef = useRef<HTMLDivElement>(null);

  const { data: threads = [], isLoading: threadsLoading } = useQuery<Thread[]>({
    queryKey: ["orin-admin", "threads"],
    queryFn: async () => {
      const res = await fetch("/api/orin-admin/threads?limit=50", { credentials: "include" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    },
    refetchInterval: 30_000,
  });

  // Auto-select first thread once loaded
  useEffect(() => {
    if (!selected && threads.length > 0) setSelected(threads[0].counterparty);
  }, [threads, selected]);

  const filteredThreads = useMemo(() => {
    if (!search.trim()) return threads;
    const q = search.toLowerCase();
    return threads.filter((t) =>
      t.counterparty_name.toLowerCase().includes(q) ||
      t.last_body_snippet.toLowerCase().includes(q)
    );
  }, [threads, search]);

  const { data: messages = [], isLoading: messagesLoading } = useQuery<Message[]>({
    queryKey: ["orin-admin", "messages", selected],
    queryFn: async () => {
      if (!selected) return [];
      const res = await fetch(`/api/orin-admin/threads/${encodeURIComponent(selected)}/messages?limit=200`, { credentials: "include" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    },
    enabled: !!selected,
    refetchInterval: 30_000,
  });

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "auto", block: "end" });
  }, [messages.length, selected]);

  const selectedThread = threads.find((t) => t.counterparty === selected);

  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-[280px_1fr]" style={{ height: "calc(100vh - 220px)" }}>
      {/* Left rail */}
      <Card className="flex flex-col overflow-hidden">
        <div className="border-b border-border p-2">
          <div className="relative">
            <Search size={14} className="pointer-events-none absolute left-2.5 top-2.5 text-muted-foreground" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search people, body…"
              className="h-8 w-full rounded-md border border-input bg-card pl-8 pr-2 text-caption focus:border-primary focus:outline-none"
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {threadsLoading && <div className="p-4 text-caption text-muted-foreground">Loading…</div>}
          {!threadsLoading && filteredThreads.length === 0 && (
            <div className="p-4 text-caption text-muted-foreground">No conversations yet.</div>
          )}
          {filteredThreads.map((t) => (
            <button
              key={t.counterparty}
              type="button"
              onClick={() => setSelected(t.counterparty)}
              className={cn(
                "flex w-full items-start gap-2 border-b border-border/40 px-3 py-2 text-left transition-colors hover:bg-muted/40",
                selected === t.counterparty && "bg-primary/5"
              )}
            >
              <Avatar name={t.counterparty_name} size="sm" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate text-caption font-medium text-foreground">{t.counterparty_name}</span>
                  <span className="shrink-0 text-[10px] text-muted-foreground">{timeAgo(t.last_message_at)}</span>
                </div>
                <div className="flex items-center gap-1">
                  <ChannelChip channel={t.channel} />
                  <span className="truncate text-micro text-muted-foreground">{t.last_body_snippet}</span>
                </div>
              </div>
              {t.unread_count > 0 && (
                <span className="shrink-0 rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-medium text-white">{t.unread_count}</span>
              )}
            </button>
          ))}
        </div>
      </Card>

      {/* Right pane */}
      <Card className="flex flex-col overflow-hidden">
        {selected ? (
          <>
            <div className="flex items-center gap-2 border-b border-border p-3">
              <Avatar name={selectedThread?.counterparty_name ?? selected} size="sm" />
              <div className="min-w-0 flex-1">
                <div className="text-body font-medium text-foreground">{selectedThread?.counterparty_name ?? selected}</div>
                <div className="text-micro text-muted-foreground">{selectedThread?.counterparty}</div>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {messagesLoading && <div className="text-caption text-muted-foreground">Loading messages…</div>}
              {!messagesLoading && messages.length === 0 && <div className="text-caption text-muted-foreground">No messages.</div>}
              {messages.map((m) => <MessageBubble key={m.id} msg={m} />)}
              <div ref={bottomRef} />
            </div>
          </>
        ) : (
          <div className="flex flex-1 items-center justify-center text-caption text-muted-foreground">
            Pick a conversation from the left to view messages.
          </div>
        )}
      </Card>
    </div>
  );
}

function IssuesTab() {
  const qc = useQueryClient();
  const [grantingId, setGrantingId] = useState<string | null>(null);

  const { data, isLoading } = useQuery<IssuesResponse>({
    queryKey: ["orin-admin", "issues"],
    queryFn: async () => {
      const res = await fetch("/api/orin-admin/issues", { credentials: "include" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    },
    refetchInterval: 30_000,
  });

  const handleGrant = async (g: IssueGrant) => {
    const email = prompt(
      `Grant Felix access for ${g.display_name ?? g.chat_user_resource}?\nEmail:`,
      g.guessed_email ?? ""
    );
    if (!email) return;
    setGrantingId(g.id);
    try {
      const res = await fetch("/api/orin-admin/grant", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ chat_user_resource: g.chat_user_resource, email, granted_by: "felix-admin" }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error ?? `HTTP ${res.status}`);
      qc.invalidateQueries({ queryKey: ["orin-admin", "issues"] });
    } catch (err) {
      alert(`Grant failed: ${err instanceof Error ? err.message : "unknown"}`);
    } finally {
      setGrantingId(null);
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="p-4">
          <div className="mb-2 flex items-center gap-2">
            <ShieldCheck size={14} className="text-amber-600" />
            <span className="text-body font-medium">Pending grants</span>
            <span className="text-micro text-muted-foreground">— users who hit a denial wall</span>
          </div>
          {isLoading && <div className="text-caption text-muted-foreground">Loading…</div>}
          {!isLoading && (data?.pending_grants ?? []).length === 0 && (
            <div className="rounded-md border border-dashed border-border bg-muted/20 px-3 py-6 text-center text-caption text-muted-foreground">
              No pending grants. 🎉
            </div>
          )}
          <ul className="divide-y divide-border">
            {(data?.pending_grants ?? []).map((g) => (
              <li key={g.id} className="flex items-center justify-between gap-3 py-2">
                <div className="min-w-0 flex-1">
                  <div className="text-caption text-foreground">{g.display_name ?? g.chat_user_resource}</div>
                  <div className="text-micro text-muted-foreground">
                    {g.guessed_email ? <>guess: <span className="font-mono">{g.guessed_email}</span> · </> : null}
                    requested {timeAgo(g.requested_at)} ago
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleGrant(g)}
                  disabled={grantingId === g.id}
                  className="inline-flex h-8 items-center gap-1.5 rounded-md bg-primary px-3 text-caption font-medium text-white hover:bg-primary/90 disabled:opacity-50"
                >
                  <CheckCircle2 size={12} /> {grantingId === g.id ? "Granting…" : "Grant"}
                </button>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4">
          <div className="mb-2 flex items-center gap-2">
            <AlertTriangle size={14} className="text-destructive" />
            <span className="text-body font-medium">Tool errors (last 24h)</span>
          </div>
          {isLoading && <div className="text-caption text-muted-foreground">Loading…</div>}
          {!isLoading && (data?.tool_errors ?? []).length === 0 && (
            <div className="rounded-md border border-dashed border-border bg-muted/20 px-3 py-6 text-center text-caption text-muted-foreground">
              No errors in the last 24 hours. 🎉
            </div>
          )}
          <ul className="divide-y divide-border">
            {(data?.tool_errors ?? []).map((e) => (
              <li key={e.id} className="flex flex-col gap-1 py-2">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-caption text-foreground">{e.tool}</span>
                  <span className="text-micro text-muted-foreground">{timeAgo(e.occurred_at)}</span>
                </div>
                <div className="rounded-md border border-destructive/30 bg-destructive/5 px-2 py-1 text-micro text-destructive">{e.error}</div>
                {e.counterparty && <div className="text-micro text-muted-foreground">via {e.counterparty}</div>}
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}

function ComposeTab() {
  const [toEmail, setToEmail] = useState("");
  const [channel, setChannel] = useState<"chat" | "slack" | "email">("chat");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; msg: string } | null>(null);

  // Load roster for autocomplete (uses existing public /api/orin/users? Or a Felix-side roster)
  const { data: users = [] } = useQuery<{ email: string; name: string }[]>({
    queryKey: ["orin-admin", "users-list"],
    queryFn: async () => {
      const res = await fetch("/api/orin-admin/users", { credentials: "include" });
      if (!res.ok) return [];
      const data = await res.json();
      return Array.isArray(data) ? data : (data.users ?? []);
    },
  });

  const canSend = toEmail.trim() && body.trim() && !sending;
  const charCount = body.length;

  const send = async () => {
    if (!canSend) return;
    setSending(true);
    setResult(null);
    try {
      const res = await fetch("/api/orin-admin/send", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          to_email: toEmail.trim(),
          channel,
          text: body,
          subject: subject || undefined,
          attribution_email: undefined, // server stamps _felix_actor
        }),
      });
      const data: SendResponse | { error?: string } = await res.json();
      if (!res.ok) throw new Error(("error" in data && data.error) || `HTTP ${res.status}`);
      const sent = data as SendResponse;
      setResult({ ok: true, msg: `Sent via ${sent.channel} · message_id: ${sent.message_id} · ${new Date(sent.sent_at).toLocaleString()}` });
      setBody("");
      setSubject("");
    } catch (err) {
      setResult({ ok: false, msg: err instanceof Error ? err.message : "Failed" });
    } finally {
      setSending(false);
    }
  };

  return (
    <Card>
      <CardContent className="space-y-4 p-5">
        <div>
          <label className="mb-1 block text-caption font-semibold text-muted-foreground">Recipient email</label>
          <input
            list="orin-admin-users"
            value={toEmail}
            onChange={(e) => setToEmail(e.target.value)}
            placeholder="someone@fello.ai"
            className="w-full rounded-md border border-input bg-card px-3 py-2 text-body focus:border-primary focus:outline-none"
          />
          <datalist id="orin-admin-users">
            {users.map((u) => <option key={u.email} value={u.email}>{u.name}</option>)}
          </datalist>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-caption font-semibold text-muted-foreground">Channel</label>
            <select
              value={channel}
              onChange={(e) => setChannel(e.target.value as typeof channel)}
              className="w-full rounded-md border border-input bg-card px-3 py-2 text-body focus:border-primary focus:outline-none"
            >
              <option value="chat">Google Chat</option>
              <option value="slack">Slack</option>
              <option value="email">Email</option>
            </select>
          </div>
          <div>
            <label className="mb-1 block text-caption font-semibold text-muted-foreground">Subject (email only)</label>
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              disabled={channel !== "email"}
              placeholder={channel === "email" ? "e.g. Quick check-in" : "—"}
              className="w-full rounded-md border border-input bg-card px-3 py-2 text-body focus:border-primary focus:outline-none disabled:opacity-50"
            />
          </div>
        </div>
        <div>
          <div className="mb-1 flex items-center justify-between">
            <label className="text-caption font-semibold text-muted-foreground">Body (markdown)</label>
            <span className="text-micro text-muted-foreground">{charCount} chars</span>
          </div>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={8}
            placeholder="What should Orin say?"
            className="w-full rounded-md border border-input bg-card px-3 py-2 text-body font-mono focus:border-primary focus:outline-none"
          />
        </div>
        <div className="flex items-center justify-between">
          <div className="text-micro text-muted-foreground">
            From: <span className="font-mono">Orin on behalf of <span className="text-foreground">you</span></span>
          </div>
          <button
            type="button"
            onClick={send}
            disabled={!canSend}
            className="inline-flex h-9 items-center gap-1.5 rounded-md bg-primary px-4 text-caption font-medium text-white hover:bg-primary/90 disabled:opacity-50"
          >
            <Send size={14} /> {sending ? "Sending…" : "Send"}
          </button>
        </div>
        {result && (
          <div className={cn(
            "flex items-start gap-2 rounded-md px-3 py-2 text-caption",
            result.ok ? "bg-emerald-50 text-emerald-700" : "bg-destructive/10 text-destructive"
          )}>
            {result.ok ? <CheckCircle2 size={14} className="mt-0.5 shrink-0" /> : <AlertTriangle size={14} className="mt-0.5 shrink-0" />}
            <span className="break-all">{result.msg}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function OrinAdminPanel() {
  const { isOrinAdmin, isLoading } = useOrinAdmin();
  const [tab, setTab] = useState<Tab>("inbox");

  if (isLoading) {
    return <div className="text-caption text-muted-foreground">Checking Orin admin access…</div>;
  }
  if (!isOrinAdmin) {
    return (
      <Card>
        <CardContent className="flex items-center gap-3 p-6 text-caption text-muted-foreground">
          <ShieldCheck size={16} /> You're signed in to Felix but not on Orin's admin allowlist. Ask an Orin admin to grant access.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="flex items-center gap-2 text-h2 font-semibold">
          <Bot size={18} /> Orin Admin
        </h2>
        <p className="mt-1 text-caption text-muted-foreground">
          God-mode view of Orin's conversations, denials, and outbound. All data lives in Orin; Felix renders.
        </p>
      </div>

      <div className="flex items-center gap-1 border-b border-border">
        {([
          { id: "inbox", label: "Inbox", icon: Inbox },
          { id: "issues", label: "Issues", icon: AlertTriangle },
          { id: "compose", label: "Compose", icon: Send },
        ] as { id: Tab; label: string; icon: typeof Inbox }[]).map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={cn(
                "inline-flex items-center gap-1.5 border-b-2 px-3 py-2 text-caption font-medium transition-colors",
                tab === t.id ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
              )}
            >
              <Icon size={14} /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "inbox" && <InboxTab />}
      {tab === "issues" && <IssuesTab />}
      {tab === "compose" && <ComposeTab />}
    </div>
  );
}
